import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { environment } from '../../../environments/environment';
import { isApiError } from '../../core/errors/api-error';
import { ApiErrorCode } from '../../core/errors/api-error-code';
import { problemCode, ProblemDetail } from '../../core/models/problem-detail.model';

/**
 * Attaches the bearer token and ends the session when the API says it is no longer valid. Only the
 * two session codes end it: a wrong current password on a self-service form, or a failed login,
 * are refusals of that one request (`WRONG_CURRENT_PASSWORD`, `INVALID_CREDENTIALS`) and leave the
 * session alone — the form shows the message.
 *
 * A 401 with no code at all is the one exception to branching on the code: it is not the
 * application answering but the security entry point or a proxy rejecting the token outright, and
 * a session whose token every request rejects is over too. Left alone, the user would see an error
 * banner on every page instead of the login screen.
 */
function isSessionRejected(error: unknown): boolean {
  return (
    error instanceof HttpErrorResponse &&
    error.status === 401 &&
    problemCode(error.error as ProblemDetail | null) === undefined
  );
}
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const token = authService.getToken();

  if (token && req.url.startsWith(environment.apiUrl)) {
    if (authService.isTokenExpired(token)) {
      // Don't send a request that is certain to fail — end the session now, so the user gets the
      // login screen instead of an error banner on every page. The synthetic error carries the
      // same body the API would have, so nothing downstream ever sees a status-only failure.
      authService.logoutDueToExpiredSession();
      return throwError(
        () =>
          new HttpErrorResponse({
            status: 401,
            url: req.url,
            error: { code: ApiErrorCode.TOKEN_EXPIRED, detail: 'Platnost přihlášení vypršela.' },
          }),
      );
    }
    req = req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
  }

  return next(req).pipe(
    catchError((error: unknown) => {
      if (isApiError(error, ApiErrorCode.TOKEN_EXPIRED)) {
        authService.logoutDueToExpiredSession();
      } else if (
        (isApiError(error, ApiErrorCode.UNAUTHENTICATED) || isSessionRejected(error)) &&
        authService.isAuthenticated()
      ) {
        authService.logout();
      }
      return throwError(() => error);
    }),
  );
};
